import { EventEmitter, Injectable } from '@angular/core';
import { Resolve } from '@angular/router';
import { Observable } from "rxjs/Observable";
import 'rxjs/add/operator/map';

import {
  ReimbursementRecordShape,
  ReimbursementStatus,
  ReimbursementStatusShape,
  ReimbursementType
} from "./itsr-api.model";
import { ItsrApiErrorResponse, ItsrApiService } from "./itsr-api.service";
import { SessionStorageService } from './sessionstorage.service';

export class AllowanceDataRecord {
  allowanceQuarterly: number = 0;
  allowanceAnnually: number = 0;
  approvedAmount: number = 0;
  pendingAmount: number = 0;
  availableAmount: number = 0;
  approvedAmountPercentage: number = 0;
  pendingAmountPercentage: number = 0;
  availableAmountPercentage: number = 100;
  totalAmount: number = 0;
  startDate: string = '';
  endDate: string = '';
}

@Injectable()
export class ReimbursmentsService {
  readonly reimbursementSubmitted: EventEmitter<any> = new EventEmitter();

  private static getDefaultAnnualAllowance(reimbursementType: ReimbursementType): number {
    switch (reimbursementType) {
      case ReimbursementType.HomeInspection: return 350;
      case ReimbursementType.ServiceGuarantee: return 2000;
      case ReimbursementType.CashBack: return 500;
    }
    return 0;
  }

  constructor(private itsrApiService: ItsrApiService, private sessionState: SessionStorageService) {
  }

  fetchAllowance(reimbursementType: ReimbursementType): Observable<AllowanceDataRecord | ItsrApiErrorResponse> {
    return this.itsrApiService.getReimbursementAllowanceList().map((response) => {
      if (ItsrApiService.isErrorResponse(response)) {
        return response;
      }

      const result = new AllowanceDataRecord();
      result.availableAmount = ReimbursmentsService.getDefaultAnnualAllowance(reimbursementType);

      const allowanceRecord = response.body.list.find((item) => {
        return item.ReimbursementType === reimbursementType;
      });

      if (allowanceRecord) {
        result.allowanceAnnually = allowanceRecord.AllowanceAnnually ||
          ReimbursmentsService.getDefaultAnnualAllowance(reimbursementType);
        result.allowanceQuarterly = allowanceRecord.AllowanceQuarterly || 0;
        result.approvedAmount = allowanceRecord.Annually.Claimed.Total;
        result.pendingAmount = allowanceRecord.Annually.PendingApproval.Total;
        result.availableAmount = allowanceRecord.Available;

        const factor = result.allowanceAnnually / 100;
        if (factor > 0) {
          result.approvedAmountPercentage = result.approvedAmount / factor;
          result.pendingAmountPercentage = result.pendingAmount / factor;
          result.availableAmountPercentage = result.availableAmount / factor;
        }

        result.totalAmount = result.allowanceAnnually;

        // ASSUMPTION: dates are ISO 8601 formatted
        result.startDate = allowanceRecord.StartDate.substring(0, 10);
        result.endDate = allowanceRecord.EndDate.substring(0, 10);
      }

      return result;
    });
  }

  decorateReimbursmentRecordList(list: ReimbursementRecordShape[], statusList: ReimbursementStatusShape[]): void {
    list.forEach((record) => {
      const status = record.Status === ReimbursementStatus.Processed ? ReimbursementStatus.Approved : record.Status;
      const statusRecord = statusList.find((item) => item.Key === status);
      record.StatusLabel = statusRecord ? <string>statusRecord.Value : 'n/a';
    });
  }
}

@Injectable()
export class HomeInspectionAllowanceResolver implements Resolve<AllowanceDataRecord> {
  constructor(private reimbursmentsService: ReimbursmentsService) { }

  resolve(): Observable<AllowanceDataRecord> {
    return this.reimbursmentsService.fetchAllowance(ReimbursementType.HomeInspection)
      .map((response) => ItsrApiService.isErrorResponse(response) ? new AllowanceDataRecord() : response);
  }
}

@Injectable()
export class ServiceGuaranteeAllowanceResolver implements Resolve<AllowanceDataRecord> {
  constructor(private reimbursmentsService: ReimbursmentsService) { }

  resolve(): Observable<AllowanceDataRecord> {
    return this.reimbursmentsService.fetchAllowance(ReimbursementType.ServiceGuarantee)
      .map((response) => ItsrApiService.isErrorResponse(response) ? new AllowanceDataRecord() : response);
  }
}

@Injectable()
export class CashBackAllowanceResolver implements Resolve<AllowanceDataRecord> {
  constructor(private reimbursmentsService: ReimbursmentsService) { }

  resolve(): Observable<AllowanceDataRecord> {
    return this.reimbursmentsService.fetchAllowance(ReimbursementType.CashBack)
      .map((response) => ItsrApiService.isErrorResponse(response) ? new AllowanceDataRecord() : response);
  }
}



// WEBPACK FOOTER //
// ./src/app/_services/reimbursments.service.ts